import React, { useState } from 'react'
import { ChevronDownIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/solid'
import { t } from 'i18next'
import { useLazyQuery } from '@apollo/client'
import { CALCULATE_BOOKING_PRICE } from 'graphql/booking/queries'
import { DepartureTripAddons, ReturnTripAddons, OneWayTripAddons } from './addonSections'
import moment from 'moment'

export const AddOns = ({
    isRoundTrip,
    departureTrip,
    tripDetails,
    filterObj,
    tripMeals,
    setTripMeals,
    selectedAddons,
    setSelectedAddons,
    addonPrices,
    addonPriceConfig,
    setAddonPriceConfig,
    openAddonSection,
    setOpenAddonSection,
    addonStep,
    setAddonStep,
    departureAddonsSelected,
    setDepartureAddonsSelected,
    mealsLoading,
    addonConfigLoading,
    getTripMeals,
    getAddOnPriceConfig,
    passengersDetails,
    setPassengersDetails,
    totalPrice,
    setTotalPrice,
    setEnableTab,
    setActiveTab
}) => {
    // Query for calculating booking cost with meals
    const [calculateBookingCost, { loading: calculationLoading }] = useLazyQuery(CALCULATE_BOOKING_PRICE, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.calculateBookingCost?.success) {
                const priceData = data.calculateBookingCost.price
                
                // Update total price with the calculated final price
                setTotalPrice(priceData.final_price)
                
                // Store the complete price breakdown in passengersDetails
                setPassengersDetails(prev => ({
                    ...prev,
                    priceBreakdown: {
                        currency: priceData.user_preferred_currency,
                        price_summary: priceData.price_summary,
                        price_summary_total: priceData.price_summary_total,
                        totalPriceBeforeDiscount: priceData.total_price_before_discount,
                        totalDiscount: priceData.total_discount,
                        totalPriceAfterDiscount: priceData.total_price_after_discount,
                        totalVat: priceData.total_vat,
                        totalPriceWithVat: priceData.total_price_with_vat,
                        totalAddOnPrice: priceData.total_add_on_price,
                        totalAddOnTax: priceData.total_add_on_tax,
                        totalAddOnPriceWithTax: priceData.total_add_on_price_with_tax,
                        finalPrice: priceData.final_price,
                        passengers: priceData.passengers,
                        meals: priceData.meals,
                        special_items: priceData.special_items,
                        extra_luggage: priceData.extra_luggage,
                        shuttle_service: priceData.shuttle_service
                    },
                    addons: {
                        selected: selectedAddons,
                        prices: addonPrices,
                        total: priceData.total_add_on_price_with_tax
                    }
                }))
                
                // Enable confirm details tab
                setEnableTab({
                    passengerInfo: true,
                    seatSelection: true,
                    addons: true,
                    confirmDetails: true,
                })
                
                // Move to confirm details tab
                setActiveTab(3)
            }
        },
        onError: (error) => {
            console.error('Error calculating booking cost:', error)
        }
    })
    // Helper function to update meal quantity
    const updateMealQuantity = (tripType, mealId, quantity) => {
        setSelectedAddons(prev => ({
            ...prev,
            [tripType]: {
                ...prev[tripType],
                meals: {
                    ...prev[tripType].meals,
                    [mealId]: quantity
                }
            }
        }))
    }
    
    // Helper function to get meal quantity
    const getMealQuantity = (tripType, mealId) => {
        return selectedAddons[tripType].meals[mealId] || 0
    }

    // Helper function to prepare meals for a trip segment
    const prepareMealsForSegment = (tripType) => {
        if (!selectedAddons[tripType]?.meals) {
            return []
        }
        
        const meals = []
        Object.entries(selectedAddons[tripType].meals).forEach(([tripMealId, quantity]) => {
            if (quantity > 0) {
                // Find the meal details to get the meal_id
                const mealDetails = tripMeals[tripType].find(meal => meal.trip_meal_id === tripMealId)
                if (mealDetails) {
                    meals.push({
                        meal_id: mealDetails.meal_id,
                        quantity: quantity
                    })
                }
            }
        })
        return meals
    }

    // Helper function to prepare extra luggage for a trip segment
    const prepareExtraLuggageForSegment = (tripType) => {
        if (!selectedAddons[tripType]?.luggage) {
            return []
        }
        
        const extraLuggage = []
        Object.entries(selectedAddons[tripType].luggage).forEach(([luggageKey, isSelected]) => {
            if (isSelected) {
                // Extract weight from luggage key (e.g., "5kg" -> 5)
                const weightKg = parseInt(luggageKey.replace('kg', ''))
                if (weightKg && addonPriceConfig.extra_luggage) {
                    // Find the luggage option in the config to get the weight
                    const luggageOption = addonPriceConfig.extra_luggage.find(l => l.weight_kg === weightKg)
                    if (luggageOption) {
                        extraLuggage.push({
                            weight_kg: luggageOption.weight_kg,
                            quantity: 1 // Each luggage selection is for 1 piece
                        })
                    }
                }
            }
        })
        return extraLuggage
    }

    // Helper function to prepare special items for a trip segment
    const prepareSpecialItemsForSegment = (tripType) => {
        if (!selectedAddons[tripType]?.special_items) {
            return []
        }
        
        const specialItems = []
        Object.entries(selectedAddons[tripType].special_items).forEach(([itemType, quantity]) => {
            if (quantity > 0) {
                specialItems.push({
                    item: itemType, // "stroller" or "wheelchair"
                    quantity: quantity
                })
            }
        })
        return specialItems
    }

    // Helper function to prepare booking data for API call
    const prepareBookingData = () => {
        // Prepare trip segments based on trip type
        const tripSegments = []
        
        if (isRoundTrip && departureTrip) {
            // Add departure trip segment
            tripSegments.push({
                segment_order: 1,
                travel_date: filterObj.form?.searchDate,
                route_id: departureTrip.route?.route_id,
                trip_id: departureTrip.trip?.trip_id,
                trip_location: {
                    start_location_id: filterObj.form?.fromLocationId,
                    end_location_id: filterObj.form?.toLocationId,
                },
                passengers: passengersDetails.passengers.map((info, index) => {
                    const dob = moment(info.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
                    return {
                        passenger: {
                            given_name: info.passenger.given_name,
                            family_name: info.passenger.family_name,
                            date_of_birth: dob,
                            nationality_code: info.passenger.nationality_code || null,
                            special_needs: info.passenger.special_needs,
                            passenger_id: "0"
                        },
                        seat_info: {
                            seat_no: info.seat_info.departure_seat_no || info.seat_info.seat_no,
                            seat_type_code: info.seat_info.departure_seat_type_code || info.seat_info.seat_type_code,
                        },
                    }
                }),
                meals: prepareMealsForSegment('departure'),
                luggage_add_on: {
                    extra_luggage: prepareExtraLuggageForSegment('departure'),
                    special_items: prepareSpecialItemsForSegment('departure')
                },
                shuttle_service_add_on: selectedAddons.departure?.shuttle_service?.selected || false
            })
            
            // Add return trip segment
            tripSegments.push({
                segment_order: 2,
                travel_date: filterObj.form?.returnDate,
                route_id: tripDetails.route?.route_id,
                trip_id: tripDetails.trip?.trip_id,
                trip_location: {
                    start_location_id: filterObj.form?.toLocationId,
                    end_location_id: filterObj.form?.fromLocationId,
                },
                passengers: passengersDetails.passengers.map((info, index) => {
                    const dob = moment(info.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
                    return {
                        passenger: {
                            given_name: info.passenger.given_name,
                            family_name: info.passenger.family_name,
                            date_of_birth: dob,
                            nationality_code: info.passenger.nationality_code || null,
                            special_needs: info.passenger.special_needs,
                            passenger_id: "0"
                        },
                        seat_info: {
                            seat_no: info.return_seat_info?.seat_no || info.seat_info.seat_no,
                            seat_type_code: info.return_seat_info?.seat_type_code || info.seat_info.seat_type_code,
                        },
                    }
                }),
                meals: prepareMealsForSegment('return'),
                luggage_add_on: {
                    extra_luggage: prepareExtraLuggageForSegment('return'),
                    special_items: prepareSpecialItemsForSegment('return')
                },
                shuttle_service_add_on: selectedAddons.return?.shuttle_service?.selected || false
            })
        } else {
            // One-way trip
            tripSegments.push({
                segment_order: 1,
                travel_date: filterObj.form?.searchDate,
                route_id: tripDetails.route?.route_id,
                trip_id: tripDetails.trip?.trip_id,
                trip_location: {
                    start_location_id: filterObj.form?.fromLocationId,
                    end_location_id: filterObj.form?.toLocationId,
                },
                passengers: passengersDetails.passengers.map((info, index) => {
                    const dob = moment(info.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
                    return {
                        passenger: {
                            given_name: info.passenger.given_name,
                            family_name: info.passenger.family_name,
                            date_of_birth: dob,
                            nationality_code: info.passenger.nationality_code || null,
                            special_needs: info.passenger.special_needs,
                            passenger_id: "0"
                        },
                        seat_info: {
                            seat_no: info.seat_info.seat_no,
                            seat_type_code: info.seat_info.seat_type_code,
                        },
                    }
                }),
                meals: prepareMealsForSegment('departure'),
                luggage_add_on: {
                    extra_luggage: prepareExtraLuggageForSegment('departure'),
                    special_items: prepareSpecialItemsForSegment('departure')
                },
                shuttle_service_add_on: selectedAddons.departure?.shuttle_service?.selected || false
            })
        }
        
        return {
            user_preferred_currency: "BHD",
            trip_segments: tripSegments
        }
    }

    // Handle continue button for round trips
    const handleRoundTripContinue = () => {
        const bookingData = prepareBookingData()
        calculateBookingCost({ variables: { booking: bookingData } })
    }

    // Handle continue button for one-way trips
    const handleOneWayTripContinue = () => {
        const bookingData = prepareBookingData()
        calculateBookingCost({ variables: { booking: bookingData } })
    }

    return (
        <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">{t('Addons & Upgrades')}</h2>
                <p className="text-gray-600 mt-2">
                    {t('Enhance your journey with additional services')}
                </p>
            </div>

            {/* Trip Type Tabs - Only show for round trips */}
            {isRoundTrip && departureTrip && (
                <div className="mb-8">
                    <div className="flex bg-gray-100 rounded-xl p-1 shadow-sm">
                        <button
                            onClick={() => {
                                setAddonStep('departure')
                                setOpenAddonSection(null) // Reset accordion state when switching tabs
                            }}
                            className={`flex-1 px-3 sm:px-6 py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 min-h-[44px] sm:min-h-auto ${
                                addonStep === 'departure'
                                    ? 'bg-white text-teal-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            {t('Departure Trip')}
                        </button>
                        <button
                            onClick={() => {
                                if (!departureAddonsSelected) {
                                    setDepartureAddonsSelected(true)
                                }
                                setAddonStep('return')
                                setOpenAddonSection(null) // Reset accordion state when switching tabs
                                // Fetch meals and add-on price config for return trip if not already loaded
                                if (tripMeals.return.length === 0 && filterObj.form?.returnDate) {
                                    getTripMeals({
                                        variables: {
                                            tripId: tripDetails.trip?.trip_id,
                                            travelDate: filterObj.form.returnDate
                                        }
                                    })
                                    
                                    // Fetch add-on price configuration for return trip
                                    getAddOnPriceConfig({
                                        variables: {
                                            routeId: tripDetails.route?.route_id,
                                            tripId: tripDetails.trip?.trip_id,
                                            tripDate: filterObj.form.returnDate
                                        }
                                    })
                                }
                            }}
                            className={`flex-1 px-3 sm:px-6 py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 min-h-[44px] sm:min-h-auto ${
                                addonStep === 'return'
                                    ? 'bg-white text-teal-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            {t('Return Trip')}
                        </button>
                    </div>
                </div>
            )}

            {isRoundTrip && departureTrip ? (
                <>
                    {addonStep === 'departure' ? (
                        <DepartureTripAddons
                            tripMeals={tripMeals}
                            mealsLoading={mealsLoading}
                            selectedAddons={selectedAddons}
                            setSelectedAddons={setSelectedAddons}
                            addonPrices={addonPrices}
                            addonPriceConfig={addonPriceConfig}
                            addonConfigLoading={addonConfigLoading}
                            passengersDetails={passengersDetails}
                            openAddonSection={openAddonSection}
                            setOpenAddonSection={setOpenAddonSection}
                            getMealQuantity={getMealQuantity}
                            updateMealQuantity={updateMealQuantity}
                            tripDetails={departureTrip}
                            departureTrip={departureTrip}
                        />
                    ) : (
                        <ReturnTripAddons
                            tripMeals={tripMeals}
                            mealsLoading={mealsLoading}
                            selectedAddons={selectedAddons}
                            setSelectedAddons={setSelectedAddons}
                            addonPrices={addonPrices}
                            addonPriceConfig={addonPriceConfig}
                            addonConfigLoading={addonConfigLoading}
                            passengersDetails={passengersDetails}
                            openAddonSection={openAddonSection}
                            setOpenAddonSection={setOpenAddonSection}
                            getMealQuantity={getMealQuantity}
                            updateMealQuantity={updateMealQuantity}
                            tripDetails={tripDetails}
                        />
                    )}
                </>
            ) : (
                <OneWayTripAddons
                    tripMeals={tripMeals}
                    mealsLoading={mealsLoading}
                    selectedAddons={selectedAddons}
                    setSelectedAddons={setSelectedAddons}
                    addonPrices={addonPrices}
                    addonPriceConfig={addonPriceConfig}
                    addonConfigLoading={addonConfigLoading}
                    passengersDetails={passengersDetails}
                    openAddonSection={openAddonSection}
                    setOpenAddonSection={setOpenAddonSection}
                    getMealQuantity={getMealQuantity}
                    updateMealQuantity={updateMealQuantity}
                    tripDetails={tripDetails}
                />
            )}

            {/* Continue Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
                {isRoundTrip && departureTrip && addonStep === 'return' ? (
                <button
                    onClick={handleRoundTripContinue}
                    disabled={calculationLoading}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-300 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none min-h-[44px] sm:min-h-auto"
                >
                    <div className="flex items-center space-x-2">
                        {calculationLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                        <span>{calculationLoading ? t('Calculating...') : t('Continue to Confirmation')}</span>
                    </div>
                </button>
                ) : !isRoundTrip || !departureTrip ? (
                    <button
                        onClick={handleOneWayTripContinue}
                        disabled={calculationLoading}
                        className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-300 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none min-h-[44px] sm:min-h-auto"
                    >
                        <div className="flex items-center space-x-2">
                            {calculationLoading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            )}
                            <span>{calculationLoading ? t('Calculating...') : t('Continue to Confirmation')}</span>
                        </div>
                </button>
                ) : null}
            </div>
        </div>
    )
}